import {
  Activity,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  Coins,
  ExternalLink,
  Gauge,
  Link2,
  LoaderCircle,
  PauseCircle,
  PlayCircle,
  PlugZap,
  Plus,
  Search,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, formatDate } from "../components/Badge";
import { Field, Input, Select, Textarea } from "../components/Form";
import { Empty, ErrorState, Failure, Loading, Success } from "../components/State";
import { api, json, queryString } from "../lib/api";
import type {
  EnterpriseProductApp,
  Page,
  ProductDefinition,
  UsageRecord,
  EnterpriseWorkspace,
} from "../lib/types";
import { useAsync } from "../lib/useAsync";

const productCategories = ["", "招聘", "投放", "电商", "人才服务", "科研写作"];
const appTabs = ["overview", "feishu", "connections", "tasks", "monitoring", "approvals", "usage", "settings"] as const;
type AppTab = (typeof appTabs)[number];
type FeishuAuthorizationState = "idle" | "authorizing" | "authorized" | "failed";

const tabLabels: Record<AppTab, string> = {
  overview: "概览",
  feishu: "飞书",
  connections: "业务连接",
  tasks: "任务",
  monitoring: "监控",
  approvals: "审批",
  usage: "用量",
  settings: "配置",
};

export function ProductsPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const products = useAsync(
    () => api<Page<ProductDefinition>>(`/products${queryString({ q: query, category })}`),
    [query, category],
  );

  return (
    <div className="inner-page product-page">
      <header className="inner-hero">
        <div>
          <span>AI PRODUCTS</span>
          <h1>AI 产品目录</h1>
          <p>选择标准产品，为自己的企业工作区创建飞书自建应用；产品运行统一消耗平台余额。</p>
        </div>
        <div className="inner-hero-mark">
          <Bot />
          <strong>OPC 原生页面</strong>
          <span>Headless Provider 接入</span>
        </div>
      </header>
      <section className="content-container">
        <div className="toolbar product-toolbar">
          <label className="toolbar-search">
            <Search size={16} />
            <input className="input search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索产品、Provider 或能力" />
          </label>
          <Select value={category} onChange={(event) => setCategory(event.target.value)}>
            {productCategories.map((item) => <option key={item || "all"} value={item}>{item || "全部分类"}</option>)}
          </Select>
          <Link className="secondary-button" to="/apps"><WalletCards size={16} />我的应用</Link>
        </div>
        {products.loading ? <Loading label="正在加载产品" /> : products.error ? <ErrorState error={products.error} retry={products.retry} /> : !products.data?.items.length ? <Empty title="没有匹配产品" detail="换个关键词或分类再试。" /> : <div className="product-grid">
          {products.data.items.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>}
      </section>
    </div>
  );
}

function ProductCard({ product }: { product: ProductDefinition }) {
  return (
    <article className="product-card">
      <header>
        <span className={`product-mark ${productMarkClass(product.id)}`}><Bot size={19} /></span>
        <div>
          <Badge tone="accent">{product.product_category || product.category}</Badge>
          <h2>{product.name}</h2>
          <p>{product.tagline}</p>
        </div>
      </header>
      <p>{product.summary}</p>
      <div className="product-meta">
        <span>{product.provider_name}</span>
        <span>v{product.release}</span>
        <span>¥{product.estimate_from.toFixed(2)} - ¥{product.estimate_to.toFixed(2)}/次</span>
      </div>
      <footer>
        <Link className="secondary-button compact" to={`/products/${product.id}`}>查看详情</Link>
        {isProductAvailable(product) ? <Link className="primary-button compact" to={`/products/${product.id}/create`}><Plus size={14} />创建应用</Link> : <span className="secondary-button compact disabled-action">接入评审中</span>}
      </footer>
    </article>
  );
}

export function ProductDetailPage() {
  const { id, productId } = useParams();
  const targetId = id || productId || "";
  const product = useAsync(() => api<ProductDefinition>(`/products/${targetId}`), [targetId]);
  if (product.loading) return <div className="detail-page"><Loading label="正在加载产品详情" /></div>;
  if (product.error) return <div className="detail-page"><ErrorState error={product.error} retry={product.retry} /></div>;
  if (!product.data) return <div className="detail-page"><Empty title="产品不存在" /></div>;

  return (
    <div className="detail-page">
      <div className="detail-container product-detail">
        <Link className="back-button" to="/products"><ArrowLeft size={15} />返回产品目录</Link>
        <article className="article-card">
          <header>
            <Badge tone="accent">{product.data.product_category || product.data.category}</Badge>
            <h1>{product.data.name}</h1>
            <p>{product.data.summary}</p>
            <dl>
              <div><dt>Provider</dt><dd>{product.data.provider_name}</dd></div>
              <div><dt>发布版本</dt><dd>v{product.data.release}</dd></div>
              <div><dt>页面形态</dt><dd>OPC 原生</dd></div>
              <div><dt>预计消耗</dt><dd>¥{product.data.estimate_from.toFixed(2)} - ¥{product.data.estimate_to.toFixed(2)}/次</dd></div>
            </dl>
            <div className="article-actions">
              <span><ShieldCheck size={16} />用户只能创建并使用自己企业的应用</span>
              {isProductAvailable(product.data) ? <Link className="primary-button" to={`/products/${product.data.id}/create`}><Plus size={16} />创建企业应用</Link> : <span className="secondary-button disabled-action">Provider 接入评审中</span>}
            </div>
          </header>
          <section className="product-detail-body">
            <Panel title="产品能力" eyebrow="CAPABILITIES">
              <div className="capability-list">
                {product.data.capabilities.map((capability) => <article key={capability.id}><Badge>{modeName(capability.mode)}</Badge><strong>{capability.label}</strong><p>{capability.description}</p></article>)}
              </div>
            </Panel>
            <Panel title="飞书能力" eyebrow="FEISHU">
              <div className="tag-list">{product.data.feishu_capabilities.map((item) => <span key={item}>{item}</span>)}</div>
            </Panel>
            <Panel title="接入定位" eyebrow="PROTOCOL">
              <div className="tag-list">{product.data.integration_surfaces.map((item) => <span key={item}>{item}</span>)}</div>
              <p className="panel-copy">产品方作为 Headless Provider 通过协议提供业务能力，OPC 负责页面、企业应用、余额、审批、飞书和审计。</p>
            </Panel>
          </section>
        </article>
      </div>
    </div>
  );
}

export function CreateProductAppPage() {
  const { id, productId } = useParams();
  const targetId = id || productId || "";
  const navigate = useNavigate();
  const product = useAsync(() => api<ProductDefinition>(`/products/${targetId}`), [targetId]);
  const workspaces = useAsync(() => api<{ items: EnterpriseWorkspace[] }>("/workspaces"), []);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ workspace_id: "", name: "", scenario: "", usable_scope: "全员可见，管理员审批高影响动作", monthly_budget_amount: 300, warning_amount: 60 });
  const [feishuState, setFeishuState] = useState<FeishuAuthorizationState>("idle");
  const [authorizedWorkspaceId, setAuthorizedWorkspaceId] = useState("");
  const [externalReady, setExternalReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const selectedWorkspace = workspaces.data?.items.find((item) => item.id === form.workspace_id) ?? workspaces.data?.items[0];

  const requiresExternalConnection = Boolean(product.data?.integration_surfaces.includes("Connection"));
  const ready = Boolean(product.data && selectedWorkspace && form.name && feishuState === "authorized" && authorizedWorkspaceId === selectedWorkspace.id && (!requiresExternalConnection || externalReady));

  const selectWorkspace = (workspaceId: string) => {
    setForm({ ...form, workspace_id: workspaceId });
    setFeishuState("idle");
    setAuthorizedWorkspaceId("");
    setExternalReady(false);
    setError("");
  };

  const authorizeFeishu = async () => {
    if (!selectedWorkspace || feishuState === "authorizing") return;
    setFeishuState("authorizing");
    setError("");
    try {
      const result = await api<{ status: string }>(`/workspaces/${selectedWorkspace.id}/feishu/authorize`, { method: "POST", body: json({}) });
      if (result.status === "authorized") {
        setAuthorizedWorkspaceId(selectedWorkspace.id);
        setFeishuState("authorized");
      } else {
        setAuthorizedWorkspaceId("");
        setFeishuState("failed");
      }
    } catch (reason) {
      setAuthorizedWorkspaceId("");
      setFeishuState("failed");
      setError(reason instanceof Error ? reason.message : "飞书授权未完成，请重试");
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!product.data || !selectedWorkspace) return;
    setBusy(true);
    setError("");
    try {
      const created = await api<EnterpriseProductApp>("/enterprise-product-apps", { method: "POST", body: json({ productId: product.data.id, workspaceId: selectedWorkspace.id, name: form.name, scenario: form.scenario, availableScope: form.usable_scope, monthlyBudgetAmount: form.monthly_budget_amount, warningAmount: form.warning_amount, externalConnection: requiresExternalConnection ? { name: externalConnectionName(product.data.id), provider: externalConnectionProvider(product.data.id), scopes: ["只读数据", "经审批执行"] } : undefined }) });
      const connected = await api<EnterpriseProductApp>(`/enterprise-product-apps/${created.id}/feishu/connect`, { method: "POST", body: json({ tenantName: `${selectedWorkspace.name}飞书企业（Mock）`, appName: form.name }) });
      navigate(`/apps/${connected.id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "创建应用失败");
    } finally {
      setBusy(false);
    }
  };

  if (product.loading || workspaces.loading) return <div className="application-page"><Loading label="正在准备创建向导" /></div>;
  if (product.error || workspaces.error) return <div className="detail-page"><ErrorState error={product.error || workspaces.error} retry={() => { product.retry(); workspaces.retry(); }} /></div>;
  if (product.data && !isProductAvailable(product.data)) return <div className="detail-page"><div className="detail-container"><Empty title="产品接入评审中" detail="Provider 协议、权限和运行状态通过审核后才能创建企业应用。" /><Link className="back-button" to={`/products/${product.data.id}`}><ArrowLeft size={15} />返回产品详情</Link></div></div>;

  return (
    <form className="application-page product-create" onSubmit={submit}>
      <aside className="application-aside">
        <span>CREATE APP</span>
        <h1>{product.data?.name || "创建应用"}</h1>
        <p>为当前账号自己的企业工作区创建飞书自建应用，凭证由 OPC 托管，运行统一消耗平台余额。</p>
        <ol>{["企业工作区", "飞书授权", "应用配置", "业务授权", "余额预警", "确认"].map((item, index) => <li key={item} className={step === index ? "active" : ""}><span>{index + 1}</span>{item}</li>)}</ol>
        <div className="application-id"><small>PRODUCT PROVIDER</small><strong>{product.data?.provider_name}</strong><Badge tone="accent">Headless Provider</Badge></div>
      </aside>
      <section className="application-form-wrap">
        <div className="application-form">
          <header><span>OPC NATIVE APPLICATION</span><h2>创建企业专属飞书应用</h2><p>不需要手动创建应用、复制 App Secret 或配置机器人权限。</p></header>
          {error && <Failure>{error}</Failure>}
          {step === 0 && <div className="form-section"><h3><ShieldCheck size={16} />选择企业工作区</h3>{!workspaces.data?.items.length ? <div className="create-prerequisite"><Empty title="还没有企业工作区" detail="先创建自己的企业工作区，再为它配置专属飞书应用。" /><Link className="primary-button" to="/workspaces">创建企业工作区</Link></div> : <Field label="企业工作区" required><Select value={selectedWorkspace?.id || ""} onChange={(event) => selectWorkspace(event.target.value)}>{workspaces.data.items.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}</Select></Field>}</div>}
          {step === 1 && <div className="form-section"><h3><Link2 size={16} />授权飞书</h3><div className={`connect-panel ${feishuState}`}><div><strong>一键创建企业专属飞书应用</strong><p>{feishuState === "authorizing" ? "正在等待企业管理员确认飞书权限。" : feishuState === "failed" ? "上次授权未完成，可以重新发起。" : `确认后将为 ${selectedWorkspace?.name || "当前企业"} 创建企业自建应用，并由 OPC 托管应用凭证。`}</p></div><button type="button" disabled={feishuState === "authorizing"} className={feishuState === "authorized" ? "secondary-button active" : "primary-button"} onClick={() => void authorizeFeishu()}>{feishuState === "authorizing" ? <LoaderCircle className="spin" size={16} /> : feishuState === "authorized" ? <CheckCircle2 size={16} /> : <ExternalLink size={16} />}{feishuState === "authorizing" ? "等待授权" : feishuState === "authorized" ? "飞书已授权" : feishuState === "failed" ? "重新授权" : "授权飞书"}</button></div><div className="connection-state-track"><span className="done">企业已选择</span><span className={feishuState === "authorizing" ? "active" : feishuState === "authorized" ? "done" : ""}>管理员授权</span><span className={feishuState === "authorized" ? "done" : ""}>等待创建应用</span></div></div>}
          {step === 2 && <div className="form-section"><h3><Bot size={16} />应用配置</h3><div className="form-grid cols-2"><Field label="应用名称" required><Input required value={form.name} placeholder={`${selectedWorkspace?.name || "企业"}${product.data?.name || "助手"}`} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field><Field label="飞书可用范围"><Input value={form.usable_scope} onChange={(event) => setForm({ ...form, usable_scope: event.target.value })} /></Field></div><Field label="业务场景"><Textarea value={form.scenario} placeholder={product.data?.tagline} onChange={(event) => setForm({ ...form, scenario: event.target.value })} /></Field></div>}
          {step === 3 && <div className="form-section"><h3><PlugZap size={16} />业务账号授权</h3>{requiresExternalConnection ? <div className="connect-panel"><div><strong>{externalConnectionName(product.data?.id || "")}</strong><p>授权在 OPC 页面完成，不会跳转或登录原产品平台。高影响写操作仍需飞书审批。</p></div><button type="button" className={externalReady ? "secondary-button active" : "primary-button"} onClick={() => setExternalReady(true)}>{externalReady ? <CheckCircle2 size={16} /> : <ExternalLink size={16} />}{externalReady ? "业务账号已连接" : "授权业务账号"}</button></div> : <div className="connect-panel complete"><div><strong>无需额外业务账号</strong><p>本产品创建后即可在本企业飞书中使用。</p></div><Badge tone="success">已满足</Badge></div>}</div>}
          {step === 4 && <div className="form-section"><h3><Gauge size={16} />余额预警</h3><div className="form-grid cols-2"><Field label="月度预算（元）"><Input type="number" min={10} value={form.monthly_budget_amount} onChange={(event) => setForm({ ...form, monthly_budget_amount: Number(event.target.value) })} /></Field><Field label="余额预警线（元）"><Input type="number" min={1} value={form.warning_amount} onChange={(event) => setForm({ ...form, warning_amount: Number(event.target.value) })} /></Field></div><p className="section-help">平台余额仅用于 OPC 服务消费，不支持交易、转账、提现或兑换现金。</p></div>}
          {step === 5 && <div className="form-section"><h3><ClipboardCheck size={16} />确认创建</h3><div className="confirm-grid"><div><dt>产品</dt><dd>{product.data?.name}</dd></div><div><dt>企业工作区</dt><dd>{selectedWorkspace?.name}</dd></div><div><dt>飞书状态</dt><dd>{feishuState === "authorized" && authorizedWorkspaceId === selectedWorkspace?.id ? "已授权" : "未授权"}</dd></div><div><dt>业务账号</dt><dd>{requiresExternalConnection ? (externalReady ? "已授权" : "未授权") : "无需授权"}</dd></div><div><dt>月度预算</dt><dd>¥{form.monthly_budget_amount.toFixed(2)}</dd></div></div></div>}
          <div className="form-actions"><button type="button" className="secondary-button" disabled={step === 0 || busy} onClick={() => setStep(Math.max(0, step - 1))}>上一步</button>{step < 5 ? <button key={`next-${step}`} type="button" className="primary-button" disabled={(step === 0 && !selectedWorkspace) || (step === 1 && (feishuState !== "authorized" || authorizedWorkspaceId !== selectedWorkspace?.id)) || (step === 2 && !form.name.trim()) || (step === 3 && requiresExternalConnection && !externalReady)} onClick={() => setStep(Math.min(5, step + 1))}>下一步</button> : <button key="submit" type="submit" className="primary-button" disabled={!ready || busy}>{busy ? "创建中..." : "创建并完成连接"}</button>}</div>
        </div>
      </section>
    </form>
  );
}

export function ProductAppsPage() {
  const apps = useAsync(() => api<{ items: EnterpriseProductApp[] }>("/enterprise-product-apps"), []);
  return (
    <div className="page-shell apps-page">
      <header className="page-heading split"><div><span>MY APPS</span><h1>我的应用</h1><p>只展示当前账号在自己企业工作区创建的飞书自建应用。</p></div><Link className="primary-button" to="/products"><Plus size={16} />创建新应用</Link></header>
      {apps.loading ? <Loading label="正在加载我的应用" /> : apps.error ? <ErrorState error={apps.error} retry={apps.retry} /> : !apps.data?.items.length ? <Empty title="还没有应用" detail="从产品目录选择产品，为自己的企业创建第一个飞书应用。" /> : <div className="app-list">{apps.data.items.map((app) => <ProductAppRow key={app.id} app={app} />)}</div>}
    </div>
  );
}

function ProductAppRow({ app }: { app: EnterpriseProductApp }) {
  const tone = app.status === "active" ? "success" : app.status === "attention_required" || app.status === "failed" ? "danger" : "warning";
  return <Link className="app-row" to={`/apps/${app.id}`}><span className="product-mark"><Bot size={18} /></span><div><strong>{app.name}</strong><small>{app.product_name} · {app.workspace_name || app.venture_name} · {app.available_scope || app.usable_scope}</small></div><Badge tone={tone}>{appStatusName(app.status)}</Badge><span>本月 ¥{app.month_cost_amount.toFixed(2)}</span></Link>;
}

export function ProductAppDetailPage() {
  const { id, appId } = useParams();
  const targetId = id || appId || "";
  const [tab, setTab] = useState<AppTab>("overview");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const app = useAsync(() => api<EnterpriseProductApp>(`/enterprise-product-apps/${targetId}`), [targetId]);

  const changeStatus = async (action: "pause" | "resume") => {
    setMessage("");
    setError("");
    try {
      await api<EnterpriseProductApp>(`/enterprise-product-apps/${targetId}/${action}`, { method: "POST" });
      setMessage(action === "pause" ? "应用已暂停" : "应用已恢复");
      app.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "更新应用状态失败");
    }
  };

  const decide = async (approvalId: string, decision: "approve" | "reject") => {
    setMessage("");
    setError("");
    try {
      await api(`/approvals/${approvalId}/confirm`, { method: "POST", body: json({ decision }) });
      setMessage(decision === "approve" ? "审批已通过" : "审批已拒绝");
      app.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "审批处理失败");
    }
  };

  const connectFeishu = async () => {
    setConnecting(true);
    setMessage("");
    setError("");
    try {
      await api<EnterpriseProductApp>(`/enterprise-product-apps/${targetId}/feishu/connect`, { method: "POST", body: json({}) });
      setMessage("飞书连接已恢复，企业应用正在运行");
      await app.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "飞书连接失败");
    } finally {
      setConnecting(false);
    }
  };

  if (app.loading) return <div className="detail-page"><Loading label="正在加载应用" /></div>;
  if (app.error) return <div className="detail-page"><ErrorState error={app.error} retry={app.retry} /></div>;
  if (!app.data) return <div className="detail-page"><Empty title="应用不存在" /></div>;
  const current = app.data;

  return (
    <div className="detail-page">
      <div className="detail-container app-detail">
        <Link className="back-button" to="/apps"><ArrowLeft size={15} />返回我的应用</Link>
        {message && <Success>{message}</Success>}
        {error && <Failure>{error}</Failure>}
        <article className="article-card">
          <header>
            <Badge tone={current.status === "active" ? "success" : "warning"}>{appStatusName(current.status)}</Badge>
            <h1>{current.name}</h1>
            <p>{current.product_name} · {current.workspace_name || current.venture_name} · {current.provider_name}</p>
            <dl><div><dt>飞书企业</dt><dd>{current.feishu_tenant_name || "未连接"}</dd></div><div><dt>可用范围</dt><dd>{current.available_scope || current.usable_scope}</dd></div><div><dt>可用余额</dt><dd>¥{current.balance_amount.toFixed(2)}</dd></div><div><dt>本月消耗</dt><dd>¥{current.month_cost_amount.toFixed(2)}</dd></div></dl>
            <div className="article-actions"><span><ShieldCheck size={16} />当前账号自己的企业应用</span>{current.feishu_status !== "connected" ? <button className="primary-button" disabled={connecting} onClick={() => void connectFeishu()}>{connecting ? <LoaderCircle className="spin" size={16} /> : <Link2 size={16} />}{connecting ? "正在重连" : "重新连接飞书"}</button> : <button className="secondary-button" onClick={() => void changeStatus(current.status === "active" ? "pause" : "resume")}>{current.status === "active" ? <PauseCircle size={16} /> : <PlayCircle size={16} />}{current.status === "active" ? "暂停应用" : "恢复应用"}</button>}</div>
          </header>
          <nav className="app-tabs">{appTabs.map((item) => <button key={item} className={tab === item ? "active" : ""} onClick={() => setTab(item)}>{tabLabels[item]}</button>)}</nav>
          <section className="app-tab-body"><AppTabPanel app={current} tab={tab} onDecide={decide} onConnect={connectFeishu} connecting={connecting} /></section>
        </article>
      </div>
    </div>
  );
}

function AppTabPanel({ app, tab, onDecide, onConnect, connecting }: { app: EnterpriseProductApp; tab: AppTab; onDecide: (id: string, decision: "approve" | "reject") => void; onConnect: () => Promise<void>; connecting: boolean }) {
  if (tab === "overview") return <div className="metric-grid"><Metric icon={<Activity />} label="运行任务" value={`${app.operations.length}`} /><Metric icon={<ClipboardCheck />} label="待审批" value={`${app.approvals.filter((item) => item.status === "pending").length}`} /><Metric icon={<Coins />} label="本月消耗" value={`¥${app.month_cost_amount.toFixed(2)}`} /></div>;
  if (tab === "feishu") return <div className="detail-list"><article><strong>连接状态</strong><div className="row-actions"><Badge tone={app.feishu_status === "connected" ? "success" : app.feishu_status === "failed" || app.feishu_status === "revoked" ? "danger" : "warning"}>{feishuConnectionName(app.feishu_status)}</Badge>{app.feishu_status !== "connected" && <button className="primary-button compact" disabled={connecting} onClick={() => void onConnect()}>{connecting ? "正在重连" : "重新连接"}</button>}</div></article><article><strong>飞书自建应用</strong><span>{app.feishu_app_name || app.name}</span></article><article><strong>凭证托管</strong><span>OPC 托管，不向用户展示 App Secret</span></article><article><strong>异常处理</strong><span>授权失效、权限不完整或创建失败时暂停任务并在此重新连接</span></article></div>;
  if (tab === "connections") return <RecordList items={app.external_connections || []} empty="本产品无需额外业务账号" render={(item) => <article key={item.id}><div><strong>{item.name}</strong><small>{item.provider || "外部业务平台"} · {(item.scopes || []).join("、")}</small></div><Badge tone={item.status === "connected" ? "success" : item.status === "expired" ? "danger" : "warning"}>{connectionName(item.status)}</Badge><span>{item.last_synced_at ? `同步于 ${formatDate(item.last_synced_at)}` : "尚未同步"}</span></article>} />;
  if (tab === "tasks") return <RecordList items={app.operations} empty="暂无任务" render={(item) => <article key={item.id}><div><strong>{item.title}</strong><small>{item.model || "provider"} · {formatDate(item.created_at)}</small></div><Badge tone={item.status === "succeeded" ? "success" : item.status === "failed" ? "danger" : "warning"}>{operationName(item.status)}</Badge><span>¥{item.cost_amount.toFixed(2)}</span></article>} />;
  if (tab === "monitoring") return <RecordList items={app.watches || []} empty="暂无持续监控" render={(item) => <article key={item.id}><div><strong>{item.name}</strong><small>{item.cadence} · 上次运行 {formatDate(item.last_run_at)}</small></div><Badge tone={item.status === "active" ? "success" : item.status === "failed" ? "danger" : "warning"}>{watchName(item.status)}</Badge><span>{item.next_run_at ? `下次 ${formatDate(item.next_run_at)}` : "未安排"}</span></article>} />;
  if (tab === "approvals") return <RecordList items={app.approvals} empty="暂无审批" render={(item) => <article key={item.id}><div><strong>{item.title}</strong><small>{item.impact} · {formatDate(item.created_at)}</small></div><Badge tone={item.status === "pending" ? "warning" : item.status === "approved" ? "success" : "danger"}>{approvalName(item.status)}</Badge>{item.status === "pending" && <div className="row-actions"><button className="secondary-button compact" onClick={() => onDecide(item.id, "reject")}>拒绝</button><button className="primary-button compact" onClick={() => onDecide(item.id, "approve")}>通过</button></div>}</article>} />;
  if (tab === "usage") return <RecordList items={app.usage} empty="暂无用量" render={(item: UsageRecord) => <article key={item.id}><div><strong>{item.model}</strong><small>{item.input_tokens.toLocaleString()} 输入 / {item.output_tokens.toLocaleString()} 输出 · {formatDate(item.created_at)}</small></div><span>{item.product_name}</span><strong>¥{item.cost_amount.toFixed(2)}</strong></article>} />;
  return <div className="detail-list"><article><strong>业务场景</strong><span>{app.scenario || "未配置"}</span></article><article><strong>飞书可用范围</strong><span>{app.available_scope || app.usable_scope}</span></article><article><strong>产品版本</strong><span>{app.release_version ? `v${app.release_version}` : "跟随当前稳定版本"}</span></article><article><strong>余额规则</strong><span>月度预算 ¥{app.monthly_budget_amount.toFixed(2)} · 低于 ¥{app.warning_amount.toFixed(2)} 预警</span></article></div>;
}

function Panel({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return <section className="white-panel product-panel"><div className="panel-heading"><span>{eyebrow}</span><h2>{title}</h2></div>{children}</section>;
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <article className="metric-card"><span>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></article>;
}

function RecordList<T>({ items, empty, render }: { items: T[]; empty: string; render: (item: T) => React.ReactNode }) {
  if (!items.length) return <Empty title={empty} detail="当前没有可展示记录。" />;
  return <div className="record-list">{items.map(render)}</div>;
}

function modeName(mode: string) {
  return { sync: "同步", async: "异步", watch: "持续监控" }[mode] || mode;
}

function appStatusName(status: string) {
  return { draft: "待连接", pending_feishu: "待飞书授权", active: "运行中", paused: "已暂停", attention_required: "需处理", failed: "创建失败" }[status] || status;
}

function operationName(status: string) {
  return { running: "运行中", waiting_approval: "待审批", succeeded: "已完成", failed: "失败" }[status] || status;
}

function approvalName(status: string) {
  return { pending: "待审批", approved: "已通过", rejected: "已拒绝" }[status] || status;
}

function watchName(status: string) {
  return { active: "运行中", paused: "已暂停", failed: "异常" }[status] || status;
}

function connectionName(status: string) {
  return { connected: "已连接", pending: "待授权", expired: "已过期", revoked: "已撤销" }[status] || status;
}

function feishuConnectionName(status: string) {
  return { not_connected: "未连接", authorizing: "授权中", pending_authorization: "待管理员授权", connected: "飞书已连接", failed: "连接失败", revoked: "授权已失效" }[status] || status;
}

function externalConnectionName(productId: string) {
  return productId === "adflow-ai" ? "广告投放账户" : productId === "commerce-ops" ? "电商店铺账户" : "外部业务账号";
}

function externalConnectionProvider(productId: string) {
  return productId === "adflow-ai" ? "巨量千川（Mock）" : productId === "commerce-ops" ? "电商平台（Mock）" : "第三方平台（Mock）";
}

function productMarkClass(productId: string) {
  return ({ "recruiting-ai": "mark-1", "adflow-ai": "mark-2", "commerce-ops": "mark-3", pageflux: "mark-4", "arxtect-ai": "mark-5" } as Record<string, string>)[productId] || "mark-4";
}

function isProductAvailable(product: ProductDefinition) {
  return product.status === "listed" || product.status === "published";
}
